<template>
  <v-container fluid class="pa-6">
    <v-card elevation="2" class="rounded-lg">
      <v-toolbar flat color="primary" dark class="rounded-t-lg">
        <v-toolbar-title class="text-h5 font-weight-bold">
          <v-icon left>mdi-account-group</v-icon>
          User Management
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn color="white" variant="elevated" @click="openCreateUserDialog">
          <v-icon left>mdi-plus</v-icon>
          Add User
        </v-btn>
      </v-toolbar>

      <!-- User Table with proper Vuetify data-table -->
      <v-data-table
        :headers="headers"
        :items="users"
        :items-per-page="10"
        class="user-table"
        hover
      >
        <template v-slot:[`item.email`]="{ item }">
          <div class="d-flex align-center">
            <v-avatar color="primary" size="32" class="mr-3">
              <span class="text-white text-caption">{{ item.email.charAt(0).toUpperCase() }}</span>
            </v-avatar>
            <span class="text-body-2">{{ item.email }}</span>
          </div>
        </template>
        <!-- Explicitly define the headers -->
        <!-- <template #headers>
          <v-data-table-headers :headers="headers"></v-data-table-headers>
        </template> -->

        <template v-slot:[`item.dataManagerRole`]="{ item }">
          <v-chip
            :color="getRoleColor(item.dataManagerRole)"
            size="small"
            class="font-weight-medium"
          >
            {{ item.dataManagerRole }}
          </v-chip>
          <v-menu offset-y>
            <template v-slot:activator="{ props }">
              <v-btn
                icon="mdi-pencil"
                size="x-small"
                variant="text"
                v-bind="props"
                class="ml-2"
              ></v-btn>
            </template>
            <v-list density="compact">
              <v-list-item
                v-for="role in roleOptions"
                :key="role"
                @click="updateUserRole(item, 'dataManagerRole', role)"
              >
                <v-list-item-title>{{ role }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <template v-slot:[`item.taxonomyManagerRole`]="{ item }">
          <v-chip
            :color="getRoleColor(item.taxonomyManagerRole)"
            size="small"
            class="font-weight-medium"
          >
            {{ item.taxonomyManagerRole }}
          </v-chip>
          <v-menu offset-y>
            <template v-slot:activator="{ props }">
              <v-btn
                icon="mdi-pencil"
                size="x-small"
                variant="text"
                v-bind="props"
                class="ml-2"
              ></v-btn>
            </template>
            <v-list density="compact">
              <v-list-item
                v-for="role in roleOptions"
                :key="role"
                @click="updateUserRole(item, 'taxonomyManagerRole', role)"
              >
                <v-list-item-title>{{ role }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex justify-center ga-2">
            <v-tooltip text="Edit User" location="top">
              <template v-slot:activator="{ props }">
                <v-btn
                  icon="mdi-pencil"
                  size="small"
                  color="primary"
                  variant="tonal"
                  v-bind="props"
                  @click="openEditDialog(item)"
                ></v-btn>
              </template>
            </v-tooltip>
            <v-tooltip text="Delete User" location="top">
              <template v-slot:activator="{ props }">
                <v-btn
                  icon="mdi-delete"
                  size="small"
                  color="error"
                  variant="tonal"
                  v-bind="props"
                  @click="confirmDeleteUser(item)"
                ></v-btn>
              </template>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="showDialog" max-width="600px">
      <v-card class="rounded-lg">
        <v-card-title class="bg-primary text-white">
          <v-icon left>{{ isEditMode ? 'mdi-account-edit' : 'mdi-account-plus' }}</v-icon>
          <span class="text-h6 font-weight-bold">
            {{ isEditMode ? 'Edit User' : 'Add New User' }}
          </span>
        </v-card-title>
        <v-card-text class="pt-6">
          <v-form ref="userForm">
            <v-text-field
              v-model="dialogUser.email"
              label="Email Address"
              :disabled="isEditMode"
              prepend-inner-icon="mdi-email"
              variant="outlined"
              required
              class="mb-4"
            ></v-text-field>

            <!-- Roles -->
            <v-select
              v-model="dialogUser.dataManagerRole"
              :items="roleOptions"
              label="Data Manager Role"
              prepend-inner-icon="mdi-shield-account"
              variant="outlined"
              required
              class="mb-4"
            ></v-select>

            <v-select
              v-model="dialogUser.taxonomyManagerRole"
              :items="roleOptions"
              label="Taxonomy Manager Role"
              prepend-inner-icon="mdi-file-tree"
              variant="outlined"
              required
              class="mb-4"
            ></v-select>

            <!-- Password (optional if editing) -->
            <v-text-field
              v-model="dialogUser.password"
              label="Password"
              type="password"
              prepend-inner-icon="mdi-lock"
              variant="outlined"
              :hint="isEditMode ? 'Leave blank to keep existing password' : 'Required for new users'"
              persistent-hint
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="elevated" @click="saveDialogUser">
            <v-icon left>mdi-content-save</v-icon>
            Save User
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="500px">
      <v-card class="rounded-lg">
        <v-card-title class="bg-error text-white">
          <v-icon left>mdi-alert</v-icon>
          <span class="text-h6 font-weight-bold">Confirm Delete</span>
        </v-card-title>
        <v-card-text class="pt-6 pb-4">
          <v-alert type="warning" variant="tonal" class="mb-4">
            This action cannot be undone!
          </v-alert>
          <p class="text-body-1">
            Are you sure you want to delete user <strong>{{ userToDelete?.email }}</strong>?
          </p>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="elevated" @click="deleteUser">
            <v-icon left>mdi-delete</v-icon>
            Delete User
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { watch } from 'vue';
import axios from 'axios';
import { getAuth } from "firebase/auth"; // or firebase.auth() if using compat
import api from "../plugins/authenticated-axios.js"
import { useProductStore } from '@/store/productStore';

export default {
  name: "FirebaseUserManager",
  data() {
    return {
      users: [],
      showDialog: false,
      isEditMode: false,
      showDeleteDialog: false,
      userToDelete: null,
      dialogUser: {
        uid: "",
        email: "",
        dataManagerRole: "read",
        taxonomyManagerRole: "read",
        password: "",
      },
      headers: [
        { title: "Email Address", key: "email", align: "start" },
        { title: "Data Manager", key: "dataManagerRole", align: "center" },
        { title: "Taxonomy Manager", key: "taxonomyManagerRole", align: "center" },
        { title: "Actions", key: "actions", align: "center", sortable: false },
      ],
      roleOptions: ["read", "write", "admin"],
      // Example: your Cloud Run URL
      cloudRunURL: "https://firebase-user-manager-998838505944.europe-west6.run.app/users",
    };
  },
  created() {
    const store = useProductStore();
    
    // solve this at the app-level
    // If auth is ready, fetch immediately
    if (store.getIsAuthReady()) {
      this.fetchUsers();
    } else {
      // Otherwise, watch for auth ready state
      const unwatch = watch(() => store.getIsAuthReady(), (isReady) => {
        if (isReady) {
          this.fetchUsers();
          unwatch(); // Stop watching once we've triggered the fetch
        }
      });
    }
  },
  methods: {
    async fetchUsers() {
      try {  
        const resp = await api.get(`${this.cloudRunURL}/list`)
        this.users = resp.data.users || [];
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    },

    openCreateUserDialog() {
      this.isEditMode = false;
      this.dialogUser = {
        uid: "",
        email: "",
        dataManagerRole: "read",
        taxonomyManagerRole: "read",
        password: "",
      };
      this.showDialog = true;
    },

    openEditDialog(user) {
      this.isEditMode = true;
      this.dialogUser = {
        uid: user.uid,
        email: user.email,
        dataManagerRole: user.dataManagerRole || "read",
        taxonomyManagerRole: user.taxonomyManagerRole || "read",
        password: "",
      };
      this.showDialog = true;
    },

    async saveDialogUser() {
      if (!this.dialogUser.email) {
        alert("Email is required");
        return;
      }
      if (this.isEditMode) {
        await this.updateUser(this.dialogUser);
      } else {
        await this.createUser(this.dialogUser);
      }
      this.showDialog = false;
      this.fetchUsers();
    },

    async createUser(newUser) {
      try {
        const auth = getAuth();
        const idToken = await auth.currentUser?.getIdToken();

        await axios.post(
          `${this.cloudRunURL}/create`,
          {
            email: newUser.email,
            password: newUser.password, // required for new user
            dataManagerRole: newUser.dataManagerRole,
            taxonomyManagerRole: newUser.taxonomyManagerRole,
          },
          {
            headers: { Authorization: `Bearer ${idToken}` },
          }
        );
      } catch (error) {
        console.error("Error creating user:", error);
        alert("Failed to create user");
      }
    },

    async updateUser(userObj) {
      try {
        const auth = getAuth();
        const idToken = await auth.currentUser?.getIdToken();

        await axios.post(
          `${this.cloudRunURL}/update`,
          {
            uid: userObj.uid,
            password: userObj.password, // optional
            dataManagerRole: userObj.dataManagerRole,
            taxonomyManagerRole: userObj.taxonomyManagerRole,
          },
          {
            headers: { Authorization: `Bearer ${idToken}` },
          }
        );
      } catch (error) {
        console.error("Error updating user:", error);
        alert("Failed to update user");
      }
    },

    async updateUserRole(user, roleField, newValue) {
      // This is if you want inline role updates in the table:
      // user.dataManagerRole or user.taxonomyManagerRole changed
      // You can either:
      // 1) Save immediately, or
      // 2) Wait for a "Save" button

      // Example: saving immediately:
      const updated = {
        ...user,
        [roleField]: newValue,
      };
      await this.updateUser(updated);
    },

    getRoleColor(role) {
      const colors = {
        admin: 'error',
        write: 'warning',
        read: 'info'
      };
      return colors[role] || 'grey';
    },

    confirmDeleteUser(user) {
      this.userToDelete = user;
      this.showDeleteDialog = true;
    },

    async deleteUser() {
      try {
        const auth = getAuth();
        const idToken = await auth.currentUser?.getIdToken();

        await axios.post(
          `${this.cloudRunURL}/delete`,
          { uid: this.userToDelete.uid },
          {
            headers: { Authorization: `Bearer ${idToken}` },
          }
        );

        this.showDeleteDialog = false;
        this.userToDelete = null;
        await this.fetchUsers();
      } catch (error) {
        console.error("Error deleting user:", error);
        alert("Failed to delete user");
      }
    },
  },
};
</script>

<style scoped>
.user-table {
  border-radius: 0 0 8px 8px;
}

:deep(.v-data-table__wrapper) {
  min-height: 400px;
}

:deep(.v-data-table-header th) {
  font-weight: 700 !important;
  font-size: 0.8125rem !important;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  background-color: #f8f9fa !important;
  color: #1976d2 !important;
  border-bottom: 2px solid #1976d2 !important;
}

:deep(.v-data-table__tr) {
  transition: all 0.2s ease;
}

:deep(.v-data-table__tr:hover) {
  background-color: #e3f2fd !important;
}

:deep(.v-chip) {
  text-transform: capitalize;
  font-weight: 600;
  letter-spacing: 0.5px;
}

:deep(.v-data-table .v-data-table__td) {
  padding: 16px !important;
}
</style>